import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import PMBInputNumber from 'components/PMBInputNumber'
import PMBSelect from 'components/PMBSelect'
import PMBTooltip from 'components/PMBTooltip'
import PMBTotalFooter from 'components/PMBTotalFooter'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, HelpCircle, Plus, Trash } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Row,
} from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { URL_CONFIGURATIONS, URL_WORKPLACE } from 'utils/pathUrl'
import { v4 as uuid } from 'uuid'
import { getDataCommodities } from './configurationsListAction'
import {
  useAddConfigurationMutation,
  useFetchConfigurationsQuery,
} from './configurationsListApiSlice'

const AddConfiguration = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const {
    data: dataConfigurations,
    isLoading,
    refetch,
    isFetching,
  } = useFetchConfigurationsQuery()

  const dataConfigurationsList = dataConfigurations?.map((item) => ({
    value: item.id,
    label: item.type,
  }))

  const [addConfiguration, { isLoading: isLoadingAddConfiguration }] =
    useAddConfigurationMutation()

  const { commodities: commoditiesList } = useSelector(
    (state) => state.configurationsList,
  )

  const { data: dataUnits } = useSelector((state) => state.publicVersion)
  const unitOptions = dataUnits?.unitArray ?? []

  const [selectedConfiguration, setSelectedConfiguration] = useState(null)
  const [isAddFromExisting, setIsAddFromExisting] = useState(false)
  const [payload, setPayload] = useState({
    id: null,
    type: null,
    commodities: [
      {
        commodity: null,
        psdh: null,
        unit: unitOptions?.[0],
      },
    ],
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setPayload({ ...payload, [name]: value })
  }

  // GET COMMODITIES
  useEffect(() => {
    dispatch(getDataCommodities())
  }, [])

  const inputRef = useRef()

  const handleCommoditiesChange = (index, field, value) => {
    setPayload((prev) => {
      const newCommodities = [...prev.commodities]
      if (field === 'psdh') {
        const replaceValue = value.replace(/[^\d,]/g, '')
        const [integerPart, decimalPart] = replaceValue.split(',')
        const formattedInteger = integerPart.replace(
          /\B(?=(\d{3})+(?!\d))/g,
          '.',
        )
        const formattedValue = decimalPart
          ? `${formattedInteger},${decimalPart}`
          : formattedInteger
        const numericValue =
          parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) ||
          null
        newCommodities[index][field] = numericValue
      } else {
        newCommodities[index][field] = value
      }
      return { ...prev, commodities: newCommodities }
    })
  }

  // AUTO FOCUS
  useEffect(() => {
    if (!payload.type) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [payload.type])

  const resetState = () => {
    setSelectedConfiguration(null)
    setIsAddFromExisting(false)
    setPayload({
      id: null,
      type: null,
      commodities: [
        {
          id: null,
          commodity: null,
          psdh: null,
          unit: unitOptions?.[0],
        },
      ],
    })
  }

  function mergePayloadByType(payloadArray) {
    const resultMap = new Map()

    for (const entry of payloadArray) {
      const { type, commodities, id } = entry

      if (!resultMap.has(type)) {
        resultMap.set(type, {
          id,
          type,
          commodities: [...commodities],
        })
      } else {
        const existing = resultMap.get(type)
        const existingCommodityIds = new Set(
          existing.commodities.map((c) => c.commodity.id),
        )

        const newCommodities = commodities.filter(
          (c) => !existingCommodityIds.has(c.commodity.id),
        )

        existing.commodities.push(...newCommodities)
      }
    }

    return Array.from(resultMap.values())
  }

  // HANDLE SUBMIT
  const handleSubmit = async () => {
    try {
      const isExistType = dataConfigurations?.find(
        (item) => item.type === payload.type,
      )

      if (isExistType) {
        enqueueSnackbar('Jenis konfigurasi telah terdaftar', {
          variant: 'error',
        })
        return
      }

      let payloadArray = []

      // CREATE MODE
      if (!isAddFromExisting) {
        const finalUuid = uuid().split('-').join('')
        const timestamp = Date.now()
        const uniqueId = finalUuid + timestamp
        payloadArray = [
          {
            ...payload,
            id: uniqueId,
            commodities: payload?.commodities?.map((item, index) => ({
              ...item,
              id: `${uniqueId}${index}`,
            })),
          },
          ...(dataConfigurations || []),
        ]

        const response = await addConfiguration(payloadArray)
        if (response && response?.data) {
          enqueueSnackbar(`Konfigurasi berhasil ditambahkan`, {
            variant: 'success',
          })
          resetState()
          navigate(`${URL_CONFIGURATIONS}?refetch=true`)
        }
      } else {
        const findData = dataConfigurations?.find(
          (item) => item.id === selectedConfiguration?.value,
        )

        if (!findData) {
          enqueueSnackbar('Konfigurasi tidak ditemukan', { variant: 'error' })
          return
        }

        const finalUuid = uuid().split('-').join('')
        const timestamp = Date.now()
        const uniqueId = finalUuid + timestamp

        const mergedCommodities = [
          ...(payload?.commodities?.map((item, index) => ({
            ...item,
            id: `${uniqueId}${index}`,
          })) || []),
          ...(findData?.commodities || []),
        ]

        const payloadArray = [
          {
            ...findData,
            id: uniqueId,
            commodities: mergedCommodities,
          },
          ...(dataConfigurations || []),
        ]

        const cleanedPayloadArray = mergePayloadByType(payloadArray)

        const response = await addConfiguration(cleanedPayloadArray)
        if (response && response?.data) {
          enqueueSnackbar('Konfigurasi berhasil ditambahkan', {
            variant: 'success',
          })
          resetState()
          navigate(`${URL_CONFIGURATIONS}?refetch=true`)
        }
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Konfigurasi gagal ditambahkan', {
        variant: 'error',
      })
    }
  }

  const isDisabled = isAddFromExisting
    ? !selectedConfiguration ||
      payload?.commodities?.some(
        (item) =>
          !item?.commodity ||
          item?.psdh === null ||
          item?.psdh === 0 ||
          !item?.unit,
      )
    : !payload?.type ||
      payload?.commodities?.some(
        (item) =>
          !item?.commodity ||
          item?.psdh === null ||
          item?.psdh === 0 ||
          !item?.unit,
      )

  return (
    <div className="w-100">
      <Container className="custom-container" style={{ paddingBottom: '80px' }}>
        <div className="mt-3">
          <BreadCrumbs
            breadCrumbParent={'Alat Bantu'}
            breadCrumbParentLink={URL_WORKPLACE}
            breadCrumbActive="Tambah Konfigurasi"
            breadCrumbParent2="Daftar Konfigurasi"
            breadCrumbParent2Link={URL_CONFIGURATIONS}
          />
        </div>

        <Card className="mb-3 border-0 shadow-sm p-2">
          <CardBody>
            <div className="d-flex align-items-center gap-2 mb-4">
              <ArrowLeft
                style={{ cursor: 'pointer' }}
                size={16}
                onClick={() => navigate(URL_CONFIGURATIONS)}
              />
              <P size="medium" className="fw-medium">
                Tambah Konfigurasi
              </P>
            </div>

            <FormGroup>
              <Row>
                <Col>
                  <div
                    onClick={() => {
                      setSelectedConfiguration(null)
                      setIsAddFromExisting(false)
                    }}
                    className={`d-flex justify-content-between align-items-center p-3 rounded border pointer ${!isAddFromExisting ? 'bg-primary-50 border-primary' : ''}`}
                  >
                    Tambah jenis baru
                    <Input
                      onClick={() => {
                        setSelectedConfiguration(null)
                        setIsAddFromExisting(false)
                      }}
                      value={isAddFromExisting}
                      checked={!isAddFromExisting}
                      type="radio"
                    />
                  </div>
                </Col>
                <Col>
                  <div
                    onClick={() => {
                      setPayload((prev) => ({
                        ...prev,
                        type: null,
                      }))
                      setIsAddFromExisting(true)
                    }}
                    className={`d-flex justify-content-between align-items-center p-3 rounded border pointer ${isAddFromExisting ? 'bg-primary-50 border-primary' : ''}`}
                  >
                    Pilih jenis
                    <Input
                      onClick={() => {
                        setPayload((prev) => ({
                          ...prev,
                          type: null,
                        }))
                        setIsAddFromExisting(true)
                      }}
                      value={isAddFromExisting}
                      checked={isAddFromExisting}
                      type="radio"
                    />
                  </div>
                </Col>
              </Row>
            </FormGroup>

            {isAddFromExisting ? (
              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Jenis<span style={{ color: '#b42218' }}>*</span>
                </P>
                <PMBSelect
                  value={selectedConfiguration}
                  options={dataConfigurationsList}
                  onChange={(e) => setSelectedConfiguration(e)}
                  getOptionLabel={(e) => e.label}
                  name="type"
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  testId="configurations-form-existing-type"
                />
              </FormGroup>
            ) : (
              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Jenis<span style={{ color: '#b42218' }}>*</span>
                </P>
                <Input
                  placeholder="Masukkan jenis"
                  name="type"
                  value={payload.type || ''}
                  onChange={handleChange}
                  autoComplete="off"
                  innerRef={inputRef}
                  data-testid="configurations-form-type"
                />
              </FormGroup>
            )}

            {payload.commodities.map((item, index) => (
              <div
                key={index}
                className={`mb-3 ${payload.commodities.length !== 0 && index !== payload.commodities.length - 1 ? 'border-bottom' : ''}`}
              >
                <FormGroup>
                  <P className="text-sm pb-1" size="regular" color="#344054">
                    Barang<span style={{ color: '#b42218' }}>*</span>
                  </P>
                  <PMBSelect
                    disabled={
                      isAddFromExisting
                        ? selectedConfiguration === null
                        : item.type === null
                    }
                    value={item.commodity}
                    options={commoditiesList}
                    onChange={(e) =>
                      handleCommoditiesChange(index, 'commodity', e)
                    }
                    getOptionLabel={(e) => e.label}
                    name="commodity"
                    menuPosition="fixed"
                    testId={`configurations-form-commodity-${index}`}
                    menuPlacement="bottom"
                    menuPortalTarget={
                      typeof window !== 'undefined' ? document.body : null
                    }
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#d1d5db',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#d1d5db',
                        },
                        borderRadius: '8px',
                        padding: '2px',
                        minWidth: '100%',
                        maxWidth: '100%',
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: '#e0f2ff',
                        borderRadius: '12px',
                        padding: '2px 6px',
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: '#0369a1',
                      }),
                      multiValueRemove: (provided) => ({
                        ...provided,
                        color: '#0369a1',
                        '&:hover': {
                          backgroundColor: '#b3e5fc',
                          color: '#005f8e',
                        },
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#6b7280',
                      }),
                      clearIndicator: (provided) => ({
                        ...provided,
                        color: '#6b7280',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        maxHeight: 120,
                        overflowY: 'auto',
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </FormGroup>

                <div className="row">
                  <div className="col-md-6">
                    <FormGroup>
                      <div className="d-flex align-items-center pb-1">
                        <P
                          className="text-sm me-1"
                          size="regular"
                          color="#344054"
                        >
                          PSDH
                        </P>
                        <HelpCircle id={`tooltip-psdh-${index}`} width={14} />
                        <span style={{ color: '#b42218' }}>*</span>
                        <PMBTooltip
                          placement="right"
                          target={`tooltip-psdh-${index}`}
                          text="PROVISI SUMBER DAYA HUTAN"
                        />
                      </div>
                      <PMBInputNumber
                        onFocus={(e) => e.target.select()}
                        disabled={
                          isAddFromExisting
                            ? selectedConfiguration === null
                            : item.type === null
                        }
                        value={formatInputNumber(item.psdh)}
                        onChange={(e) =>
                          handleCommoditiesChange(index, 'psdh', e.target.value)
                        }
                        name="psdh"
                        placeholder="Masukkan psdh"
                        prefix="Rp"
                        isFirstPrefix
                        testId={`configurations-form-psdh-${index}`}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <P
                        className="text-sm pb-1"
                        size="regular"
                        color="#344054"
                      >
                        Satuan<span style={{ color: '#b42218' }}>*</span>
                      </P>
                      <PMBSelect
                        disabled={
                          isAddFromExisting
                            ? selectedConfiguration === null
                            : item.type === null
                        }
                        value={item.unit}
                        options={unitOptions}
                        onChange={(e) =>
                          handleCommoditiesChange(index, 'unit', e)
                        }
                        name="unit"
                        menuPosition="fixed"
                        menuPlacement="bottom"
                        testId={`configurations-form-unit-${index}`}
                        menuPortalTarget={
                          typeof window !== 'undefined' ? document.body : null
                        }
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderColor: '#d1d5db',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#d1d5db',
                            },
                            borderRadius: '8px',
                            padding: '2px',
                            minWidth: '100%',
                            maxWidth: '100%',
                          }),
                          multiValue: (provided) => ({
                            ...provided,
                            backgroundColor: '#e0f2ff',
                            borderRadius: '12px',
                            padding: '2px 6px',
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: '#0369a1',
                          }),
                          multiValueRemove: (provided) => ({
                            ...provided,
                            color: '#0369a1',
                            '&:hover': {
                              backgroundColor: '#b3e5fc',
                              color: '#005f8e',
                            },
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#6b7280',
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            color: '#6b7280',
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            maxHeight: 120,
                            overflowY: 'auto',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                      />
                    </FormGroup>
                  </div>
                </div>

                {payload.commodities.length > 1 && (
                  <div className="d-flex justify-content-end mb-2">
                    <CustomButton
                      className="mb-2"
                      size="sm"
                      outline
                      color="danger"
                      onClick={() => {
                        setPayload((prev) => {
                          const newCommodities = [...prev.commodities]
                          newCommodities.splice(index, 1)
                          return { ...prev, commodities: newCommodities }
                        })
                      }}
                      testId={`configurations-form-remove-${index}`}
                    >
                      <Trash size={16} /> Hapus
                    </CustomButton>
                  </div>
                )}
              </div>
            ))}

            <CustomButton
              disabled={isDisabled}
              size="sm"
              style={{ maxWidth: 186 }}
              outline
              color="primary"
              onClick={() => {
                setPayload((prev) => ({
                  ...prev,
                  commodities: [
                    ...prev.commodities,
                    { commodity: null, psdh: null, unit: unitOptions?.[0] },
                  ],
                }))
              }}
              testId="configurations-add-commodity-button"
            >
              <Plus size={16} /> Tambah Barang
            </CustomButton>
          </CardBody>
        </Card>
      </Container>

      <PMBTotalFooter
        isDisable={isDisabled}
        onSubmit={handleSubmit}
        buttonOnly
        customTotalText={
          <div>
            <P>Seluruh data konfigurasi wajib diisi!</P>
          </div>
        }
        isLoading={isLoadingAddConfiguration}
        textBtn={'Simpan'}
        testId="configurations-submit-button"
      />
    </div>
  )
}

export default AddConfiguration
